import type { SettingsTemplateModel } from '@/shared/generated'

import React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Slot } from 'radix-ui'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useCreateSettingsTemplateMutation } from '@/entities/settings-template'

import { client } from '@/shared/api/client'
import { Button } from '@/shared/components/ui/button'
import { SettingsTemplateType } from '@/shared/generated'
import { ampli } from '@/shared/lib/ampli'
import { useModals } from '@/shared/lib/modal-manager'
import { processingMessage } from '@/shared/tools/message'

export const SavePricingCampaignAsTemplate = ({
	pricingCampaignId,
	children,
}: {
	pricingCampaignId: string
	children: React.ReactNode
}) => {
	const { t } = useTranslation()

	const templateType = SettingsTemplateType.PricingCampaign
	const queryClient = useQueryClient()
	const { showModal } = useModals()
	const handleOpenTemplate = ({ id }: { id: string }) => {
		showModal('SETTINGS_TEMPLATE_MODAL', {
			id,
			template_type: templateType,
		})
	}
	const createTemplateMutation = useCreateSettingsTemplateMutation({
		onMutate() {
			toast.loading(processingMessage(t), { id: 'saving_template' })
		},
		onSuccess({ id, name }: SettingsTemplateModel) {
			queryClient.invalidateQueries({ queryKey: ['settings-templates'] })
			toast.success(
				`${t('templates', { defaultValue: 'Template' })} ${t('template_saved_successfully', { defaultValue: 'was saved successfully' })}`,
				{
					description: name,
					action: (
						<Button size='sm' onClick={() => handleOpenTemplate({ id })}>
							{t('general_open', { defaultValue: 'Open' })}
						</Button>
					),
					duration: 5000,
				}
			)
		},
		onSettled() {
			toast.dismiss('saving_template')
		},
	})
	const handleClick = async () => {
		ampli.settingsDropdownSaveSettingAsTemplate()
		const pricingCampaign = await client.pricingCampaign.pricingCampaignGet({
			pricingCampaignId,
		})

		createTemplateMutation.mutate({
			template_type: SettingsTemplateType.PricingCampaign,
			name: pricingCampaign.name,
			body: { settings: pricingCampaign.settings, name: pricingCampaign.name },
		})
	}

	return <Slot.Root onClick={handleClick}>{children}</Slot.Root>
}
